import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  FaBoxOpen, FaTicketAlt, FaWonSign,
} from 'react-icons/fa';
import '../css/Main.css';

// ===== 공통 상수/유틸 =====
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  window.__API_BASE__ ||
  'http://13.124.224.246:7778';

const COLORS = ['#00C49F', '#FF8042', '#0088FE'];
const formatCurrency = (amount) =>
  new Intl.NumberFormat('ko-KR').format(amount || 0) + ' 원';

const Main = () => {
  const now = new Date();

  const [totalProductsCount, setTotalProductsCount] = useState(0);
  const [paymentStats, setPaymentStats] = useState({ card: 0, mixed: 0, point: 0 });
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [thisMonthOrderCount, setThisMonthOrderCount] = useState(0);
  const [revenueByDate, setRevenueByDate] = useState({ daily: [], monthly: [], yearly: [] });

  const [viewMode, setViewMode] = useState('daily');
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

  // Product.jsx와 동일한 토큰 헤더 구성
  const authHeaders = useMemo(() => {
    const raw = localStorage.getItem('token');
    const token = raw && raw !== 'undefined' && raw !== 'null' ? raw : '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // axios 인스턴스 (필요 시 응답 인터셉터로 401 처리)
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_BASE,
      headers: { Accept: 'application/json', ...authHeaders },
      withCredentials: false,
    });
    instance.interceptors.response.use(
      (res) => res,
      (err) => {
        const { response } = err || {};
        if (response?.status === 401) {
          // 서버에서 TOKEN_EXPIRED 등 코드 내려주면 메시지 노출
          const code = response.data?.code || '';
          if (code === 'TOKEN_EXPIRED') {
            console.warn('액세스 토큰 만료. 다시 로그인해주세요.');
            // 필요 시: 리프레시 플로우 트리거 or 로그인 페이지 이동
            // window.location.href = '/login';
          } else {
            console.warn('인증 실패. 다시 로그인해주세요.');
          }
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [authHeaders]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!authHeaders.Authorization) {
          console.warn('토큰이 없어 API 호출을 생략합니다.');
          return;
        }

        const [productRes, orderRes] = await Promise.all([
          api.get('/api/products/allProduct'),
          api.get('/api/orders/all'),
        ]);

        // 제품 수
        if (productRes.data?.success && Array.isArray(productRes.data.products)) {
          setTotalProductsCount(productRes.data.products.length);
        }

        // 주문/매출 통계
        if (orderRes.data?.success && Array.isArray(orderRes.data.orders)) {
          let monthlyTotal = 0;
          let orderCount = 0;

          const dailyMap = {};
          const monthlyMap = {};
          const yearlyMap = {};

          const addToMap = (map, key, amount, type) => {
            if (!map[key]) map[key] = { amount: 0, card: 0, point: 0, mixed: 0 };
            map[key].amount += amount;
            if (type === 'card') map[key].card += amount;
            if (type === 'point') map[key].point += amount;
            if (type === 'mixed') map[key].mixed += amount;
          };

          orderRes.data.orders.forEach((order) => {
            const date = new Date(order.createdAt);
            const amount = (order.paymentAmount || 0) + (order.pointUsed || 0);
            const type = (order.paymentType || '').toLowerCase();

            // 이번 달 KPI
            if (date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()) {
              monthlyTotal += amount;
              orderCount += 1;
            }

            const dayKey = date.toISOString().slice(0, 10);
            const monthKey = date.toISOString().slice(0, 7);
            const yearKey = date.getFullYear();

            addToMap(dailyMap, dayKey, amount, type);
            addToMap(monthlyMap, monthKey, amount, type);
            addToMap(yearlyMap, yearKey, amount, type);
          });

          const toArray = (map) =>
            Object.entries(map)
              .map(([date, value]) => ({ date, ...value }))
              .sort((a, b) => new Date(a.date) - new Date(b.date));

          setMonthlyRevenue(monthlyTotal);
          setThisMonthOrderCount(orderCount);
          setPaymentStats({
            card: Object.values(dailyMap).reduce((sum, d) => sum + (d.card || 0), 0),
            mixed: Object.values(dailyMap).reduce((sum, d) => sum + (d.mixed || 0), 0),
            point: Object.values(dailyMap).reduce((sum, d) => sum + (d.point || 0), 0),
          });
          setRevenueByDate({
            daily: toArray(dailyMap),
            monthly: toArray(monthlyMap),
            yearly: toArray(yearlyMap),
          });
        }
      } catch (err) {
        console.error('데이터 로딩 실패:', err);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, authHeaders.Authorization]);

  const kpis = [
    { label: '이번 달 매출', value: formatCurrency(monthlyRevenue), icon: <FaWonSign />, bg: 'main-section1-item-circle4' },
    { label: '이번 달 결제 건수', value: `${thisMonthOrderCount} 건`, icon: <FaTicketAlt />, bg: 'main-section1-item-circle1' },
    { label: '상품 개수', value: totalProductsCount, icon: <FaBoxOpen />, bg: 'main-section1-item-circle2' },
  ];

  const filteredRevenue = (revenueByDate[viewMode] || []).filter((item) => {
    const date = new Date(item.date);
    if (viewMode === 'daily') {
      return date.getFullYear() === selectedYear && date.getMonth() + 1 === selectedMonth;
    }
    if (viewMode === 'monthly') {
      return date.getFullYear() === selectedYear;
    }
    return true;
  });

  const pieData = [
    { name: '카드', value: paymentStats.card },
    { name: '혼합', value: paymentStats.mixed },
    { name: '포인트', value: paymentStats.point },
  ];
  const totalPie = pieData.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="main-container">
      <div className="main-container-header">
        <h1>통계</h1>
      </div>

      <div className="main-container-container">
        <div className="main-section1">
          <div className="main-section1-item-container">
            {kpis.map((kpi, idx) => (
              <div className="main-section1-item" key={idx}>
                <div className="main-section1-item-text">{kpi.label}</div>
                <div className="main-section1-item-percent">
                  <div className={kpi.bg}><span>{kpi.icon}</span></div>
                  <div className="main-section1-item-detail">{kpi.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="main-section2">
          {/* 결제 수단 비율 */}
          <div className="main-section2-graph">
            <div className="main-section2-graph-top-container">
              <div className="main-section2-graph-title">결제 수단 비율</div>
            </div>
            <div className="main-section2-graph-container">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={80}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      `${formatCurrency(value)} (${((value / (totalPie || 1)) * 100).toFixed(1)}%)`,
                      name,
                    ]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 매출 추이 */}
          <div className="main-section2-graph">
            <div className="main-section2-graph-top-container">
              <div className="main-section2-graph-title">매출 추이</div>
              <select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
                <option value="daily">일별</option>
                <option value="monthly">월별</option>
                <option value="yearly">연도별</option>
              </select>

              {viewMode === 'daily' && (
                <>
                  <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                    {[...Array(5)].map((_, i) => {
                      const year = now.getFullYear() - i;
                      return <option key={year} value={year}>{year}년</option>;
                    })}
                  </select>
                  <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}월</option>
                    ))}
                  </select>
                </>
              )}

              {viewMode === 'monthly' && (
                <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                  {[...Array(5)].map((_, i) => {
                    const year = now.getFullYear() - i;
                    return <option key={year} value={year}>{year}년</option>;
                  })}
                </select>
              )}
            </div>

            <div className="main-section2-graph-container">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={filteredRevenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div style={{ background: '#fff', border: '1px solid #ccc', padding: 10 }}>
                            <strong>{label}</strong>
                            <p>금액: {formatCurrency(data.amount)}</p>
                            <p>카드: {formatCurrency(data.card || 0)}</p>
                            <p>포인트: {formatCurrency(data.point || 0)}</p>
                            <p>혼합: {formatCurrency(data.mixed || 0)}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Line type="monotone" dataKey="amount" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Main;
